// src/components/DynamicForm/DynamicForm.tsx

import * as React from "react";
import {
  FormRequestPayload,
  FormState,
  FormSubmissionRequest,
  isFormSubmissionError,
} from "../../interfaces/formTypes";
import { submitWorkflowForm, FormSubmissionApiError } from "../../services/formSubmissionService";
import { useAuth } from "../../contexts/AuthContext";
import { createLogger } from "../../utils/logger";
import { getDocumentUrl } from "../../helpers/word";
import FormField from "./FormField";

const log = createLogger("DynamicForm");

/**
 * Props for the DynamicForm component
 */
export interface DynamicFormProps {
  payload: FormRequestPayload;
  onSubmitSuccess?: (message?: string, submittedData?: Record<string, string>) => void;
  onCancel?: () => void;
  isReadOnly?: boolean;
}

/**
 * DynamicForm component
 *
 * Renders a dynamic form based on the FormRequestPayload from the backend.
 * Handles form state, validation, and submission.
 */
const DynamicForm: React.FC<DynamicFormProps> = ({
  payload,
  onSubmitSuccess,
  onCancel,
  isReadOnly = false,
}) => {
  const { accessToken } = useAuth();

  // Form state
  const [formState, setFormState] = React.useState<FormState>(
    isReadOnly ? "submitted" : "editable"
  );
  const [fieldValues, setFieldValues] = React.useState<Record<string, string>>(() => {
    // Initialize with values from payload
    const initial: Record<string, string> = {};
    payload.fields.forEach((field) => {
      initial[field.id] = field.value || field.defaultValue || "";
    });
    return initial;
  });
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>({});
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [successMessage, setSuccessMessage] = React.useState<string | null>(null);

  // Log document URL when form appears (for debugging)
  React.useEffect(() => {
    const rawUrl = Office.context?.document?.url || null;
    const transformedUrl = getDocumentUrl();
    log.info("Document URL preview (form mounted)", {
      rawUrl,
      transformedUrl,
    });
  }, []);

  // Handle field value changes
  const handleFieldChange = React.useCallback((id: string, value: string) => {
    setFieldValues((prev) => ({ ...prev, [id]: value }));
    // Clear field error when user starts typing
    setFieldErrors((prev) => {
      if (prev[id]) {
        const next = { ...prev };
        delete next[id];
        return next;
      }
      return prev;
    });
  }, []);

  // Validate required fields
  const validateForm = React.useCallback((): boolean => {
    const errors: Record<string, string> = {};

    payload.fields.forEach((field) => {
      if (field.required && !fieldValues[field.id]?.trim()) {
        errors[field.id] = `${field.label} is required`;
      }

      // Run additional validation rules if present
      if (field.validation && fieldValues[field.id]) {
        field.validation.forEach((rule) => {
          const value = fieldValues[field.id];

          switch (rule.type) {
            case "minLength":
              if (value.length < Number(rule.value)) {
                errors[field.id] = rule.message;
              }
              break;
            case "maxLength":
              if (value.length > Number(rule.value)) {
                errors[field.id] = rule.message;
              }
              break;
            case "pattern":
              if (!new RegExp(String(rule.value)).test(value)) {
                errors[field.id] = rule.message;
              }
              break;
            case "min":
              if (Number(value) < Number(rule.value)) {
                errors[field.id] = rule.message;
              }
              break;
            case "max":
              if (Number(value) > Number(rule.value)) {
                errors[field.id] = rule.message;
              }
              break;
          }
        });
      }
    });

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }, [payload.fields, fieldValues]);

  // Handle form submission
  const handleSubmit = React.useCallback(async () => {
    log.info("Form submission initiated", {
      formType: payload.formType,
      formInstanceId: payload.formInstanceId,
    });

    // Validate
    if (!validateForm()) {
      log.warn("Form validation failed", { fieldErrors });
      return;
    }

    setFormState("submitting");
    setSubmitError(null);

    try {
      const documentUrl = getDocumentUrl();
      log.info("Document URL for workflow submission", {
        documentUrl,
        rawUrl: Office.context?.document?.url || null,
      });
      const request: FormSubmissionRequest = {
        formType: payload.formType,
        formInstanceId: payload.formInstanceId,
        conversationId: payload.conversationId,
        data: fieldValues,
        documentUrl,
      };

      const response = await submitWorkflowForm(
        payload.submission.endpoint,
        request,
        accessToken || undefined
      );

      if (isFormSubmissionError(response)) {
        // Handle structured error response
        setFormState("error");
        setSubmitError(response.error);
        if (response.fieldErrors) {
          setFieldErrors(response.fieldErrors);
        }
        log.error("Form submission returned error", {
          error: response.error,
          fieldErrors: response.fieldErrors,
        });
      } else {
        // Success
        setFormState("submitted");
        setSuccessMessage(response.message || "Form submitted successfully");
        log.info("Form submitted successfully", {
          formInstanceId: payload.formInstanceId,
          message: response.message,
        });

        if (onSubmitSuccess) {
          onSubmitSuccess(response.message, fieldValues);
        }
      }
    } catch (error) {
      setFormState("error");

      if (error instanceof FormSubmissionApiError) {
        setSubmitError(error.message);
        if (error.fieldErrors) {
          setFieldErrors(error.fieldErrors);
        }
      } else {
        setSubmitError("An unexpected error occurred. Please try again.");
      }

      log.error("Form submission failed", {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }, [payload, fieldValues, validateForm, accessToken, onSubmitSuccess, fieldErrors]);

  // Handle cancel
  const handleCancel = React.useCallback(() => {
    setFormState("cancelled");
    log.info("Form cancelled", {
      formInstanceId: payload.formInstanceId,
    });

    if (onCancel) {
      onCancel();
    }
  }, [payload.formInstanceId, onCancel]);

  // Determine if form is interactive
  const isDisabled =
    formState === "submitting" ||
    formState === "submitted" ||
    formState === "cancelled" ||
    isReadOnly;

  // Container styles
  const containerStyle: React.CSSProperties = {
    border: "1px solid #E0E0E0",
    borderRadius: "8px",
    backgroundColor: "#FFFFFF",
    padding: "16px",
    marginTop: "12px",
    marginBottom: "12px",
  };

  // Header styles
  const headerStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "16px",
    paddingBottom: "12px",
    borderBottom: "1px solid #F0F0F0",
  };

  const titleStyle: React.CSSProperties = {
    fontSize: "15px",
    fontWeight: 600,
    color: "#333",
    margin: 0,
  };

  // Status badge styles
  const getStatusBadge = () => {
    const baseStyle: React.CSSProperties = {
      fontSize: "11px",
      fontWeight: 500,
      padding: "3px 8px",
      borderRadius: "4px",
    };

    switch (formState) {
      case "submitted":
        return (
          <span style={{ ...baseStyle, backgroundColor: "#E8F5E9", color: "#2E7D32" }}>
            Submitted
          </span>
        );
      case "cancelled":
        return (
          <span style={{ ...baseStyle, backgroundColor: "#FAFAFA", color: "#757575" }}>
            Cancelled
          </span>
        );
      case "error":
        return (
          <span style={{ ...baseStyle, backgroundColor: "#FFEBEE", color: "#C62828" }}>Error</span>
        );
      default:
        return null;
    }
  };

  // Fields container - flex wrap for half-width fields
  const fieldsContainerStyle: React.CSSProperties = {
    display: "flex",
    flexWrap: "wrap",
    gap: "12px",
  };

  // Error message styles
  const errorContainerStyle: React.CSSProperties = {
    backgroundColor: "#FFEBEE",
    border: "1px solid #FFCDD2",
    borderRadius: "4px",
    padding: "10px 12px",
    marginBottom: "12px",
  };

  const errorTextStyle: React.CSSProperties = {
    fontSize: "13px",
    color: "#C62828",
    margin: 0,
  };

  // Success message styles
  const successContainerStyle: React.CSSProperties = {
    backgroundColor: "#E8F5E9",
    border: "1px solid #C8E6C9",
    borderRadius: "4px",
    padding: "10px 12px",
    marginBottom: "12px",
  };

  const successTextStyle: React.CSSProperties = {
    fontSize: "13px",
    color: "#2E7D32",
    margin: 0,
  };

  // Button container styles
  const buttonContainerStyle: React.CSSProperties = {
    display: "flex",
    gap: "8px",
    justifyContent: "flex-end",
    marginTop: "16px",
    paddingTop: "12px",
    borderTop: "1px solid #F0F0F0",
  };

  // Button styles
  const primaryButtonStyle: React.CSSProperties = {
    padding: "8px 16px",
    fontSize: "13px",
    fontWeight: 500,
    color: "#FFFFFF",
    backgroundColor: formState === "submitting" ? "#90CAF9" : "#1976D2",
    border: "none",
    borderRadius: "4px",
    cursor: formState === "submitting" ? "not-allowed" : "pointer",
    transition: "background-color 0.2s",
  };

  const secondaryButtonStyle: React.CSSProperties = {
    padding: "8px 16px",
    fontSize: "13px",
    fontWeight: 500,
    color: "#666",
    backgroundColor: "#F5F5F5",
    border: "1px solid #E0E0E0",
    borderRadius: "4px",
    cursor: "pointer",
    transition: "background-color 0.2s",
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <h3 style={titleStyle}>{payload.display.title}</h3>
        {getStatusBadge()}
      </div>

      {/* Error message */}
      {submitError && formState === "error" && (
        <div style={errorContainerStyle}>
          <p style={errorTextStyle}>{submitError}</p>
        </div>
      )}

      {/* Success message */}
      {successMessage && formState === "submitted" && (
        <div style={successContainerStyle}>
          <p style={successTextStyle}>{successMessage}</p>
        </div>
      )}

      {/* Form fields */}
      <div style={fieldsContainerStyle}>
        {payload.fields.map((field) => (
          <FormField
            key={field.id}
            field={field}
            value={fieldValues[field.id] || ""}
            onChange={handleFieldChange}
            error={fieldErrors[field.id]}
            disabled={isDisabled}
          />
        ))}
      </div>

      {/* Action buttons - only show if form is still interactive */}
      {formState === "submitting" && (
        <div style={buttonContainerStyle}>
          <button type="button" style={secondaryButtonStyle} disabled>
            Cancel
          </button>
          <button type="button" style={primaryButtonStyle} disabled>
            Submitting...
          </button>
        </div>
      )}
      {(formState === "editable" || formState === "error") && (
        <div style={buttonContainerStyle}>
          <button type="button" onClick={handleCancel} style={secondaryButtonStyle}>
            Cancel
          </button>
          <button type="button" onClick={handleSubmit} style={primaryButtonStyle}>
            {payload.display.submitButtonText || "Submit"}
          </button>
        </div>
      )}
    </div>
  );
};

export default DynamicForm;
